import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';
import cn from 'classnames';
import Bullets from 'slickpay/common/Bullets.component';

import { fePolicyFocusNote, fePolicyFoldOpen } from 'actions/ui.actions';
import ICO_NOTES from 'assets/images/icons/pencil-notes-black.svg';
import SLICK_LOGO from 'assets/images/logos/powered-by-slick.png';
import ICO_CAL from 'assets/images/online-booking/ico-calendar-square.svg';
import ICO_HOME from 'assets/images/online-booking/ico-home-square.svg';
import ICO_BOOK from 'assets/images/online-booking/ico-notes-square.svg';
import ICO_SCISSORS from 'assets/images/online-booking/ico-scissors-square.svg';
import Button from 'components/form/Button.component';
import Note from 'components/form/Note.component';
import Foldout from 'components/generic/Foldout.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import ResponsiveBox from 'components/generic/ResponsiveBox.component';
import CharacterLimit from 'components/messages/CharacterLimit.component';
import LINK from 'constants/link.const';
import useResettingState from 'hooks/useResettingState.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useNcobSettings } from 'providers/NcobSettings.provider';
import { useSalonBasicInfo } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToString from 'utils/converters/fromAnyToString';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import linkTo from 'utils/links/linkTo.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';

import SectionContent from 'settings/online-booking/pages/SectionContent.component';
import SectionSeparator from 'settings/online-booking/pages/SectionSeparator.component';

import CN from './EmailNotificationBookingConfirmation.module.scss';

const L = logger('EmailNotificationBookingConfirmation');

const CHAR_LIMIT = 500;

const getBullets = (reminders, t) => [
  <div className={CN.bullet}>
    <span className={CN.emoji}>{t('onlineBooking.emailNotifications.clientConfirmation.bulletPoint1')}</span>
  </div>,
  <div className={CN.bullet}>
    <span className={CN.emoji}>{t('onlineBooking.emailNotifications.clientConfirmation.bulletPoint2')}</span>
  </div>,
  <div className={CN.bullet}>
    <span className={CN.emoji}>
      {t('onlineBooking.emailNotifications.clientConfirmation.bulletPoint3')}
      <NavLink to={reminders} className={CN.navLink}>
        {t('common.buttons.here')}
      </NavLink>
    </span>
  </div>,
];

const EmailNotificationBookingConfirmation = ({ fePolicyFoldOpen, fePolicyFocusNote }) => {
  const { isDarkMode } = useTheme();
  const history = useHistory();
  const { t } = useTranslation();

  const nav = navFor('MobileStylistPanel.handleWeekView()', { history });

  const { salonId, salonName, salonLogo, address1, salonCity, postcode } = useSalonBasicInfo();
  const { value, isLoading, error, update } = useNcobSettings();
  const { salonNcobInfo } = value ?? {};

  const [text, setText] = useResettingState(salonNcobInfo, useResettingState.NIL);

  const { val: isEditing, inv: isNotEditing, yay: startEditing, nay: stopEditing } = useTip(false);

  const isEmpty = fromAnyToString(text).length === 0;
  const isOverLimit = fromAnyToString(text).length > CHAR_LIMIT;
  const isDisabled = isNotEditing || isEmpty || isOverLimit;

  const remindersLink = linkTo({ pattern: LINK.reminders, params: { salonId }, fault: '#' });
  const brandingLink = linkTo({ pattern: LINK.salonOnlineBranding, params: { salonId }, fault: '#' });
  const salonHoursLink = linkTo({ pattern: LINK.salonHours, params: { salonId }, fault: '#' });

  const handleSave = useCallback(async () => {
    const { ok, value } = await update({ data: { salonNcobInfo: text } });
    if (!ok) return;
    setText(fromAnyToString(value.salonNcobInfo));
    stopEditing();
  }, [update, text, setText, stopEditing]);

  const handleTextChange = useCallback(
    (ev) => {
      setText(fromAnyToString(ev?.raw));
      startEditing();
    },
    [setText, startEditing],
  );

  const handleEditClick = useCallback(() => {
    startEditing();
  }, [startEditing]);

  const handleGoToPolicy = useCallback(() => {
    nav.push({
      pathname: linkTo({ pattern: LINK.salonOnlinePolicy, params: { salonId } }),
    });
    fePolicyFoldOpen();
    fePolicyFocusNote();
  }, [fePolicyFoldOpen, fePolicyFocusNote, nav, salonId]);

  if (error) L.debug('()', { isLoading, text, error, value });

  return (
    <div
      data-bem="EmailNotificationBookingConfirmation__component"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      {isLoading && <OverlaySpinner />}
      <Foldout
        tag="EmailNotificationBookingConfirmation__fold"
        src={ICO_NOTES}
        title={t('onlineBooking.emailNotifications.clientConfirmation.foldoutTitle')}
        width="lg"
        isOpen
      >
        <SectionSeparator />

        <SectionContent>
          <div data-bem="EmailNotificationBookingConfirmation__content" className={CN.content}>
            {t('onlineBooking.emailNotifications.clientConfirmation.description1')}
            <div className={CN.bulletsTitle}>
              {t('onlineBooking.emailNotifications.clientConfirmation.bulletsHeader')}
            </div>
            <Bullets data={getBullets(remindersLink, t)} empty compact tag="EmailNotificationBookingConfirmation" />
            <div className={CN.templateTitle}>
              {t('onlineBooking.emailNotifications.clientConfirmation.exampleTemplateTitle')}
            </div>
            <div data-bem="EmailNotificationBookingConfirmation__frame" className={CN.frame}>
              <div className={CN.frameHeader}>
                <span className={CN.frameDot} /> <span className={CN.frameDot} /> <span className={CN.frameDot} />
              </div>
              <div data-bem="EmailNotificationBookingConfirmation__row--branding" className={CN.rowBranding}>
                <div className={CN.logoContainer}>
                  <div className={CN.salonName}>
                    {salonLogo ? (
                      <img
                        src={salonLogo}
                        className={CN.salonLogo}
                        alt={t('onlineBooking.emailNotifications.clientConfirmation.salonLogo')}
                      />
                    ) : (
                      salonName
                    )}
                  </div>
                  <img
                    src={SLICK_LOGO}
                    alt={t('onlineBooking.emailNotifications.clientConfirmation.slickLogo')}
                    className={CN.slickLogoImage}
                  />
                </div>

                <NavLink to={brandingLink} className={CN.frameNavLink}>
                  <ResponsiveBox.Desktop>
                    {t('onlineBooking.emailNotifications.clientConfirmation.editBranding').toUpperCase()}
                  </ResponsiveBox.Desktop>
                  <ResponsiveBox.Mobile>
                    <Icon variant="pencil" width="lg" />
                  </ResponsiveBox.Mobile>
                </NavLink>
              </div>
              <div className={CN.frameContainer}>
                <div data-bem="EmailNotificationBookingConfirmation__row--client" className={CN.rowClient}>
                  {t('onlineBooking.emailNotifications.clientConfirmation.exampleDescriptionPart1')}{' '}
                  <span className={CN.placeholder}>
                    {t('onlineBooking.emailNotifications.clientConfirmation.exampleDescriptionPart2')}
                  </span>
                  {t('onlineBooking.emailNotifications.clientConfirmation.exampleDescriptionPart3', {
                    name: salonName,
                  })}{' '}
                  <br />
                  {t('onlineBooking.emailNotifications.clientConfirmation.exampleDescriptionPart4')}
                </div>
                <div data-bem="EmailNotificationBookingConfirmation__row--info" className={CN.rowInfo}>
                  <div className={CN.flexCol}>
                    <img
                      src={ICO_CAL}
                      alt={t('onlineBooking.emailNotifications.clientConfirmation.dateAppointment')}
                      className={CN.icon}
                    />
                    <div className={CN.rowInfoTitle}>
                      {t('onlineBooking.emailNotifications.clientConfirmation.dateAppointment')}
                      <span>{t('onlineBooking.emailNotifications.clientConfirmation.startEndTime')}</span>
                    </div>
                  </div>
                </div>
                <div data-bem="EmailNotificationBookingConfirmation__row--info" className={CN.rowInfo}>
                  <div className={CN.flexCol}>
                    <img
                      src={ICO_HOME}
                      alt={t('onlineBooking.emailNotifications.clientConfirmation.addressIcon')}
                      className={CN.icon}
                    />
                    <div
                      className={cn({
                        [CN.rowInfoTitle]: true,
                        [CN.dynamic]: true,
                      })}
                    >
                      {salonName}
                      <span>
                        {address1}, {salonCity} {postcode}
                      </span>
                    </div>
                  </div>

                  <NavLink to={salonHoursLink} className={CN.frameNavLink}>
                    <ResponsiveBox.Desktop>
                      {t('onlineBooking.emailNotifications.clientConfirmation.editBusInfo').toUpperCase()}
                    </ResponsiveBox.Desktop>
                    <ResponsiveBox.Mobile>
                      <Icon variant="pencil" width="lg" />
                    </ResponsiveBox.Mobile>
                  </NavLink>
                </div>
                <div data-bem="EmailNotificationBookingConfirmation__row--info" className={CN.rowInfo}>
                  <div className={CN.flexCol}>
                    <img
                      src={ICO_SCISSORS}
                      alt={t('onlineBooking.emailNotifications.clientConfirmation.servicesIcon')}
                      className={CN.icon}
                    />
                    <div className={CN.rowInfoTitle}>
                      {t('onlineBooking.emailNotifications.clientConfirmation.serviceName')}
                      <span>{t('onlineBooking.emailNotifications.clientConfirmation.serviceNameInfo')}</span>
                    </div>
                  </div>
                  <span className={CN.placeholder}>
                    {t('onlineBooking.emailNotifications.clientConfirmation.price')}
                  </span>
                </div>
                <div data-bem="EmailNotificationBookingConfirmation__row--info" className={CN.rowInfo}>
                  <div className={CN.flexCol}>
                    <img
                      src={ICO_BOOK}
                      alt={t('onlineBooking.emailNotifications.clientConfirmation.notesIcon')}
                      className={CN.icon}
                    />
                    <div className={CN.rowInfoTitle}>
                      {t('onlineBooking.emailNotifications.clientConfirmation.notesVoucherCode')}
                      <span>{t('onlineBooking.emailNotifications.clientConfirmation.none')}</span>
                    </div>
                  </div>
                </div>
                <div
                  data-bem="EmailNotificationBookingConfirmation__row--total"
                  className={cn({
                    [CN.rowTotal]: true,
                    [CN.first]: true,
                  })}
                >
                  {t('onlineBooking.emailNotifications.clientConfirmation.total')}
                  <div className={CN.placeholder}>
                    {t('onlineBooking.emailNotifications.clientConfirmation.totalAmount')}
                  </div>
                </div>
                <div data-bem="EmailNotificationBookingConfirmation__row--total" className={CN.rowTotal}>
                  {t('onlineBooking.emailNotifications.clientConfirmation.balance')}
                  <div className={CN.placeholder}>
                    {t('onlineBooking.emailNotifications.clientConfirmation.amount')}
                  </div>
                </div>
                <div className={CN.separatorContainer}>
                  <SectionSeparator />
                </div>

                <div className={CN.rowEditSomething}>
                  {t('onlineBooking.emailNotifications.clientConfirmation.cancellationPolicy')}
                  <div className={CN.policyButtonContainer}>
                    <Button
                      variant="secondary"
                      tag="EmailNotificationBookingConfirmation__edit-policy"
                      onClick={handleGoToPolicy}
                    >
                      <div className={CN.editLink}>
                        {t('onlineBooking.emailNotifications.clientConfirmation.editPolicy').toUpperCase()}
                      </div>
                    </Button>
                  </div>
                </div>
                <div className={CN.rowEditSomething}>
                  {t('onlineBooking.emailNotifications.clientConfirmation.importantInformation')}
                  <div className={CN.policyButtonContainer}>
                    <Button
                      variant="secondary"
                      tag="EmailNotificationBookingConfirmation__edit-info"
                      onClick={handleEditClick}
                    >
                      <div className={CN.editLink}>
                        {t('onlineBooking.emailNotifications.clientConfirmation.editInfo').toUpperCase()}
                      </div>
                    </Button>
                  </div>
                </div>
                <div className={CN.rowEditSomething}>
                  {t('onlineBooking.emailNotifications.clientConfirmation.amendAppointment')}
                </div>
              </div>
            </div>
            <div className={CN.noteTitle}>{t('onlineBooking.emailNotifications.clientConfirmation.noteTitle')}</div>
            <div className={CN.noteText}>
              {t('onlineBooking.emailNotifications.clientConfirmation.noteDescription')}
            </div>
            <div data-bem="EmailNotificationBookingConfirmation__note">
              <Note
                autofocus={isEditing}
                autoscroll
                hasExternalValue
                name="note"
                onChange={handleTextChange}
                tag="EmailNotificationBookingConfirmation__note"
                value={text}
                variant="expanded"
                placeholder={t('onlineBooking.emailNotifications.clientConfirmation.notePlaceholder')}
              />

              <div className={CN.buttonContainer}>
                <small className={CN.noteInfo}>
                  <CharacterLimit value={text} limit={500} />
                </small>
                <div
                  className={cn({
                    [CN.displayButton]: true,
                    [CN.hidden]: isNotEditing || isEmpty,
                  })}
                >
                  <Button
                    variant="primary"
                    tag="EmailNotificationBookingConfirmation__save--button"
                    onClick={handleSave}
                    disabled={isDisabled}
                    xSpace
                  >
                    {t('onlineBooking.emailNotifications.clientConfirmation.saveButton')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </SectionContent>
      </Foldout>
    </div>
  );
};

const EmailNotificationBookingConfirmationHoc = connect(
  null,
  mapActionsToDispatchToProps({
    fePolicyFoldOpen,
    fePolicyFocusNote,
  }),
)(EmailNotificationBookingConfirmation);

export default EmailNotificationBookingConfirmationHoc;
